import { useMutation } from '@tanstack/react-query';

import {
  UpdateTimelineContainerBody,
  UpdateTimelineContainerResponse,
} from '../../../../../../typings/api/skymap/rest/v0/timeline-containers';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { isDefined } from '../../../../utils/variables';

type Props = {
  onSuccess?: (result?: UpdateTimelineContainerResponse['data']) => void;
};

type OnUpdateTimelineParameters = {
  containerId: string;
  dateIsoString?: string;
  scanId?: string | null;
  tiledModelId?: string | null;
  orthoTileId?: string | null;
  pointCloudId?: string | null;
  comment?: string | null;
};

const useUpdateProjectTimeline = (props: Props) =>
  useMutation({
    mutationFn: async ({
      containerId,
      dateIsoString,
      scanId,
      tiledModelId,
      orthoTileId,
      pointCloudId,
      comment,
    }: OnUpdateTimelineParameters) => {
      /**
       *  This is not optimal, but there is not general "update-timeline-container" endpoint.
       *  So for the least friction approach, reuse existing endpoints, and await all at the same time.
       */
      const timelineService = SkyMapAxiosServiceFactory.instance.createTimelineServiceV0();

      const updates: Promise<UpdateTimelineContainerResponse>[] = [];

      if (isDefined(dateIsoString) || comment !== undefined) {
        const updateBody: UpdateTimelineContainerBody = {};
        if (isDefined(dateIsoString)) {
          updateBody.collectionDate = dateIsoString;
        }
        if (comment !== undefined) {
          updateBody.comment = comment ?? '';
        }

        updates.push(
          timelineService.updateTimelineContainer({ path: { containerId }, body: updateBody }),
        );
      }

      if (scanId !== undefined) {
        updates.push(
          timelineService.setScanTimelineContainer({ path: { containerId }, body: { scanId } }),
        );
      }

      if (tiledModelId !== undefined) {
        updates.push(
          timelineService.setTiledModelTimelineContainer({
            path: { containerId },
            body: { tiledModelId },
          }),
        );
      }

      if (orthoTileId !== undefined) {
        updates.push(
          timelineService.setOrthotileTimelineContainer({
            path: { containerId },
            body: { orthotileId: orthoTileId },
          }),
        );
      }

      if (pointCloudId !== undefined) {
        updates.push(
          timelineService.updateAssociatedPointCloud({
            path: { timelineId: containerId },
            body: { pointCloudId },
          }),
        );
      }

      const responses = await Promise.all(updates);

      if (responses.length === 0) {
        return undefined;
      }

      const result: UpdateTimelineContainerResponse['data'] = {
        id: containerId,
        collectionDate: dateIsoString ?? responses[0].data.collectionDate,
        comment: comment === undefined ? responses[0].data.comment : comment,
        orthotile: orthoTileId === undefined ? responses[0].data.orthotile : orthoTileId,
        scan: scanId === undefined ? responses[0].data.scan : scanId,
        pointCloud: pointCloudId === undefined ? responses[0].data.pointCloud : pointCloudId,
        tiledModelId: tiledModelId === undefined ? responses[0].data.tiledModelId : tiledModelId,
      };

      return result;
    },
    retry: false,
    networkMode: 'always',
    onSuccess: props?.onSuccess,
  });

export { useUpdateProjectTimeline };
